/* eslint-disable @typescript-eslint/naming-convention */
import { useState, useCallback, useMemo } from "react";

import { datadogLogs } from "@datadog/browser-logs";
import { Emptyable } from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { AmplitudeEvents } from "../../constants/amplitude";
import { useCurrentUserContext } from "../../contexts/CurrentUserContext";
import { RecaptchaAction } from "../../enums/recaptcha";
import { setAuthAndRedirectToApp } from "../../helpers/auth";
import { getCookieByName } from "../../helpers/cookie";
import { trpc } from "../../helpers/trpc";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { useCanny } from "../../hooks/useCanny";
import { useGtm } from "../../hooks/useGtm";
import { useRecaptchaRetry } from "../../hooks/useRecaptchaRetry";
import { useTranslate } from "../../hooks/useTranslate";

import { SignInInput } from "./types";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { ChevronLeft } from "@/icons/outlined";

declare global {
  interface Window {
    GetTelemetryID: (args: { publicToken: string; submitURL: string }) => string;
  }
}

export enum SignInStep {
  CheckEmail = "checkEmail",
  EnterPassword = "enterPassword",
  ResetPassword = "resetPassword",
  DonorLogin = "DonorLogin",
  TwoFactorAuth = "TwoFactorAuth",
  EnterSMSCode = "EnterSMSCode",
}

const getQueryParams = (url: string) => {
  const searchParams = url.split("?")[1];
  const params = new URLSearchParams(searchParams);
  const result: Record<string, string> = {};

  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
};

export const enableAuthenticator = () =>
  getCookieByName("enableAuthenticator") === "true" && process.env.NEXT_PUBLIC_APP_ENV !== "production";

export const useSignIn = () => {
  const { logAmplitudeEvent } = useAmplitude();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const { executeTrpc } = useRecaptchaRetry();
  const { redirectToCanny } = useCanny();
  const { pushGtmLogin } = useGtm();
  const { canny, redirectionUrl } = query as {
    canny: "true" | null;
    redirectionUrl: string | null;
  };

  const decodedRedirectionUrl = redirectionUrl ? decodeURIComponent(redirectionUrl) : null;

  const targetOrganizationId = decodedRedirectionUrl
    ? getQueryParams(decodedRedirectionUrl).targetOrganizationId
    : null;

  const { t } = useTranslate();

  // Check if we should use the new authenticator

  const { mutateAsync: signIn } = trpc.signIn.useMutation();
  const { mutateAsync: _signIn } = trpc._signIn.useMutation();

  const { refetchUser } = useCurrentUserContext();

  const [signInStep, setSignInStep] = useState<SignInStep>(SignInStep.CheckEmail);

  const { email: defaultEmail } = query as {
    redirection: string | null;
    email: string | undefined;
  };
  const [email, setEmail] = useState<string>(defaultEmail || "");
  const [password, setPassword] = useState<string>("");
  const [stytchVerdict, setStytchVerdict] = useState<Emptyable<string>>(null);
  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState<string>("***-***-****");

  const handleChangeEmail = (email: string) => {
    setEmail(email);
  };

  const redirectToResetPassword = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.PasswordResetStarted);
    setSignInStep(SignInStep.ResetPassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToTwoFactorAuth = useCallback(() => {
    setSignInStep(SignInStep.TwoFactorAuth);
  }, []);

  const goToSMSCode = useCallback(() => {
    setSignInStep(SignInStep.EnterSMSCode);
  }, []);

  const redirectToLogin = useCallback(() => {
    setSignInStep(SignInStep.CheckEmail);
  }, []);

  const displayBackButton = signInStep === SignInStep.EnterPassword || signInStep === SignInStep.TwoFactorAuth;

  const loginBack = useCallback(() => {
    logAmplitudeEvent(AmplitudeEvents.LoginClickBack, { initialStep: signInStep });
    if (signInStep === SignInStep.EnterPassword) {
      setSignInStep(SignInStep.CheckEmail);
    }
    if (signInStep === SignInStep.TwoFactorAuth) {
      setSignInStep(SignInStep.EnterPassword);
    }
  }, [logAmplitudeEvent, signInStep]);

  const loginBackButton = useMemo(() => {
    return (
      <DashboardButton vibe="neutral" startIcon={<ChevronLeft />} onClick={loginBack}>
        {t("dashboard", "common.back")}
      </DashboardButton>
    );
  }, [loginBack, t]);

  const getTelemetryID = async (): Promise<string | null> => {
    const STYTCH_PUBLIC_TOKEN = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;

    if (!STYTCH_PUBLIC_TOKEN) {
      datadogLogs.logger.error("[useSignIn] STYTCH_PUBLIC_TOKEN is not defined");
      return null;
    }

    if (typeof window === "undefined" || typeof window.GetTelemetryID !== "function") {
      datadogLogs.logger.error("[useSignIn] GetTelemetryID is not defined", {
        getTelemetryID: window.GetTelemetryID,
      });
      return null;
    }

    try {
      const telemetryId: string = await Promise.race([
        window.GetTelemetryID({
          publicToken: STYTCH_PUBLIC_TOKEN,
          // It's required to use the auth.zeffy.com domain to make the Device Fingerprintering work.
          submitURL: `https://auth.zeffy.com/submit`,
        }),
        new Promise<string>((_, reject) => setTimeout(() => reject(new Error("GetTelemetryID timed out")), 3000)),
      ]);

      return telemetryId;
    } catch (error) {
      datadogLogs.logger.error("[useSignIn] GetTelemetryID failed", {
        error: JSON.stringify(error),
      });
      return null;
    }
  };

  const signin = useCallback(
    async (signInInput: SignInInput): Promise<{ code: string } | null> => {
      const STYTCH_PUBLIC_TOKEN = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;

      const telemetryId = STYTCH_PUBLIC_TOKEN ? await getTelemetryID() : null;
      const action = RecaptchaAction.SignIn;
      const email = signInInput.email;
      signInInput.targetOrganizationId = targetOrganizationId;

      if (enableAuthenticator()) {
        const signInResponse = await executeTrpc(action, email, _signIn, {
          ...signInInput,
          telemetryId,
          redirectionUrl: decodedRedirectionUrl,
          email: email || "",
          password: password || "",
        });
        // Handle 2FA SMS required (comes as an error with special code)
        if (signInResponse?.error?.code === "sms_code_required") {
          const error = signInResponse?.error as { maskedPhoneNumber?: string; stytchVerdict?: string };
          setMaskedPhoneNumber(error.maskedPhoneNumber || "***-***-****");
          setStytchVerdict(error.stytchVerdict || "TELEMETRY_ID_MISSING");
          goToSMSCode();
          return null;
        }

        if (signInResponse?.error?.code) {
          return { code: signInResponse?.error?.code || "" };
        }

        if (signInResponse?.data) {
          if (canny === "true") {
            redirectToCanny();
            return null;
          }

          pushGtmLogin({ email: signInInput.email || "" });
          if (signInInput.smsCode) {
            logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, { moment: "login" });
          }

          window.location.href = signInResponse.data.redirectionUrl || "";
          return null;
        }

        return null;
      }

      const signInResponse = await executeTrpc(action, email, signIn, { ...signInInput, telemetryId });
      if (signInResponse?.data) {
        // We fetch the current user to avoid any issue with the current user context.
        await refetchUser();

        setMaskedPhoneNumber(signInResponse.data.maskedPhoneNumber || "***-***-****");
        if (signInResponse?.data?.shouldAuthenticateWithTwoFactor) {
          goToTwoFactorAuth();
        }
        if (signInResponse?.data?.shouldAuthenticateBySms) {
          setStytchVerdict(signInResponse?.data?.stytchVerdict);
          goToSMSCode();
        }
        if (signInResponse?.data?.role) {
          logAmplitudeEvent(AmplitudeEvents.UserSignInCompleted, {
            "2fa_used": false,
            sso: Boolean(signInInput.googleToken).toString(),
            sso_type: signInInput.googleToken ? "Google" : null,
          });
          if (canny === "true") {
            redirectToCanny();
            return null;
          }

          const redirection = decodedRedirectionUrl || signInResponse?.data?.redirectUrl;

          pushGtmLogin({ email: signInResponse.data.email || "" });

          if (signInInput.smsCode) {
            logAmplitudeEvent(AmplitudeEvents.TwoFASuccessful, { moment: "login" });
          }

          setAuthAndRedirectToApp({
            role: signInResponse.data.role,
            redirectionUrl: redirection,
            reload: true,
            userPermissions: signInResponse.data.permissions,
          });

          return { code: signInResponse?.error?.code || "" };
        }
      }
      return { code: signInResponse?.error?.code || "" };
    },
    [
      targetOrganizationId,
      executeTrpc,
      signIn,
      _signIn,
      decodedRedirectionUrl,
      password,
      goToSMSCode,
      canny,
      pushGtmLogin,
      redirectToCanny,
      logAmplitudeEvent,
      refetchUser,
      goToTwoFactorAuth,
    ]
  );

  return {
    signInStep,
    setSignInStep,
    email,
    handleChangeEmail,
    redirectToResetPassword,
    redirectToLogin,
    password,
    setPassword,
    displayBackButton,
    loginBack,
    loginBackButton,
    signin,
    stytchVerdict,
    maskedPhoneNumber,
  };
};
